import React, { useContext, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Link } from 'react-router'
import moment from 'moment'

import { Icon, Badge, Text, Row, Tooltip, TooltipTrigger, TooltipContent } from '@evertel/web/ui'
import { ProfilePic } from '@evertel/web/user'
import { RoomContext } from '../RoomContext'
import { useService } from '@evertel/di'
import { DepartmentController } from '@evertel/department'
import { BlueUserStore, CurrentUserController } from '@evertel/blue-user'


const MAX_VISIBLE_ICONS = 3
const SHOW_ALL_THRESHOLD = MAX_VISIBLE_ICONS

const RoomHeader: React.FC = observer(() => {

    const { roomController, userSettingsController } = useContext(RoomContext)
    const room = roomController.room
    const repliesToMessage = roomController.repliesToMessage

    const departmentController = useService(DepartmentController, [room?.departmentId])
    const userStore = useService(BlueUserStore, [])

    useEffect(() => {
        if (!room?.departmentId) return
        departmentController.init(room?.departmentId)
    }, [room?.departmentId])


    if (!room) return null

    const user = (repliesToMessage?.ownerId) ? userStore.findById(repliesToMessage?.ownerId) : null

    const name = repliesToMessage?.isRetracted ? 'Retracted' : `${user?.firstName} ${user?.lastName}`

    const roomNotificationSettings = userSettingsController.roomNotificationSettings[room.id]
    const roomNotificationsEnabled = (roomNotificationSettings) ? roomNotificationSettings.roomMessage?.enabled : true
    const replyNotificationEnabled = repliesToMessage?.meta?.repliesSubscription?.notify ?? roomNotificationsEnabled
    const isPrivate = !room.options?.departmentMembersCanJoinAsMember

    return (
        <Row valign="center" className="page-toolbar flex-shrink-0">
            {roomController.repliesToId && (
                <>
                    <Link to={`/room/${room.id}`} className="ms-n2" id="reply-header-back-button">
                        <Icon name="arrow-left" type="regular" fixedWidth={true} size={18} className="p-2 d-inline-block"/>
                    </Link>

                    {user && roomController.repliesToMessage ? (
                        <Row className="w-100">
                            <h5 className="d-flex flex-wrap replies-header-title text-truncate weight-900 m-0">
                                <span>{room.name}</span>
                                <span><span className="mx-2">·</span>Reply to {name}</span>
                            </h5>

                            {!replyNotificationEnabled && (
                                <Icon
                                    className="ms-3"
                                    name="bell-slash"
                                    size={13}
                                />
                            )}
                        </Row>
                    ) : (
                        <h5 className="weight-900 m-0">
                            {roomController.isLoadingReplies ? (
                                <span>Loading...</span>
                            ) : (
                                <span>Invalid Replies Message</span>
                            )}
                        </h5>
                    )}
                </>
            )}
            {!roomController.repliesToId &&
                <div className="d-flex align-items-center w-100">
                    <div className="flex-shrink-0 ms-n2">
                        <Icon name="hashtag" fixedWidth={true} size={18} className="p-2 d-inline-block"/>
                    </div>
                    <h5 className={classNames('m-0 me-2 weight-900 text-truncate min-width-0', {'text-decoration-line-through': !!room.isArchived})} >
                        {room.name || 'No Name'}
                    </h5>
                    <div className="d-flex align-items-center flex-shrink-0">
                        {(roomController.isInteragnecy) &&
                            <Badge color="primary">
                                {departmentController.shortName}
                            </Badge>
                        }
                        {(!!isPrivate) &&
                         <Icon
                             name="lock"
                             size={13}
                             className="px-2"
                         />
                        }
                        {(room.isSearchable) &&
                            <Badge
                                color="blue"
                                className="me-2">
                                <Icon
                                    name="bolt"
                                    size={13}
                                    className="pe-2"
                                />
                                Incident Command
                            </Badge>
                        }
                        {(roomController.isInteragnecy) &&
                            <Icon
                                name="handshake"
                                color="success"
                                size={13}
                                className="px-2"
                            />
                        }
                        {(!roomNotificationsEnabled) &&
                            <Icon
                                name="bell-slash"
                                size={13}
                                className="px-2"
                            />
                        }
                        {(!!room.isArchived) &&
                            <Badge
                                color="danger"
                                className="me-2">
                                Archived
                            </Badge>
                        }
                    </div>
                    {(!!room.description && !roomController?.repliesToId) &&
                        <Text
                            size="small"
                            className="text-truncate min-width-0 flex-grow-1">
                            {room.description}
                        </Text>
                    }
                    <div className="ms-auto flex-shrink-0">
                        <HeaderConnectorIndicators />
                    </div>
                </div>
            }
        </Row>
    )
})


const HeaderConnectorIndicators: React.FC = observer(() => {
    const currentUserController = useService(CurrentUserController, [])

    const {
        roomConnectorsController,
        roomController,
        toggleAside
    } = useContext(RoomContext)

    const { enabledConnectors, hasActiveConnectors } = roomConnectorsController
    const isExecutive = currentUserController.canExecutiveInDepartment(roomController?.room?.departmentId)

    if (!hasActiveConnectors) return null

    const handleClick = () => {
        toggleAside(isExecutive ? 'connectors' : 'settings', 'open')
    }

    // Fallback while integration names are loading.  This shouldn't ever happen?
    if (enabledConnectors.length === 0) {
        return (
            <Icon
                name="share-nodes"
                size={13}
                className="px-2"
            />
        )
    }

    const showAll = enabledConnectors.length <= SHOW_ALL_THRESHOLD
    const visibleConnectors = showAll ? enabledConnectors : enabledConnectors.slice(0, MAX_VISIBLE_ICONS)
    const overflowCount = enabledConnectors.length - MAX_VISIBLE_ICONS

    return (
        <div
            className="header-connector-indicators"
            onClick={handleClick}
        >
            {visibleConnectors.map(connector => (
                <Tooltip key={connector.id}>
                    <TooltipTrigger asChild>
                        <div className="connector-icon">
                            <ProfilePic
                                userId={connector.integrationUserId}
                                className="connector-icon"
                                width={28}
                                height={28}
                            />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>{connector.name}</TooltipContent>
                </Tooltip>
            ))}
            {!showAll && overflowCount > 0 && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="header-connector-pill">
                            +{overflowCount}
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>{overflowCount} more connector{overflowCount > 1 ? 's' : ''}</TooltipContent>
                </Tooltip>
            )}
            <span className="header-connector-label">Connectors</span>
        </div>
    )
})


RoomHeader.displayName = 'RoomHeader'
HeaderConnectorIndicators.displayName = 'HeaderConnectorIndicators'

export { RoomHeader }
